import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface EngineNextAction {
  action: string;
  reason: string | null;
  module_key: string | null;
  lesson_id: string | null;
  route: string | null;
  is_locked: boolean;
  lock_reason: string | null;
  support_level: string | null;
  audit_code: string | null;
  decided_at: string;
}

export interface MasteryEntry {
  label: string | null;
  score: number;
  confidence: number;
  last_seen_at: string | null;
}

export interface EngineQuizAttempt {
  lesson_id: string | null;
  score_percent: number | null;
  passed: boolean | null;
  attempt_number: number | null;
  created_at: string;
}

export interface EngineLearningEvent {
  event_type: string;
  lesson_id: string | null;
  module_slug: string | null;
  correctness: boolean | null;
  created_at: string;
}

export interface ParentHubEngineSummary {
  learner_id: string;
  generated_at: string;
  status_label: "not_started" | "inactive" | "needs_review" | "ready_to_advance" | "active" | "in_progress" | string;
  last_activity_at: string | null;
  current_next_action: EngineNextAction | null;
  completed_lessons_count: number;
  completed_modules_count: number;
  mastery_strengths: MasteryEntry[];
  mastery_needs_focus: MasteryEntry[];
  recent_quiz_attempts: EngineQuizAttempt[];
  recent_learning_events: EngineLearningEvent[];
  support_usage_30d: Record<string, number>;
  parent_recommendation: string;
}

/**
 * Engine-backed Parent Hub summary. Calls the `get_parent_hub_learning_summary`
 * SECURITY DEFINER RPC which enforces parent/self/admin access server-side.
 */
export function useParentEngineSummary(childId: string | null | undefined) {
  const [data, setData] = useState<ParentHubEngineSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(!!childId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!childId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error } = await supabase.rpc(
        "get_parent_hub_learning_summary",
        { p_learner_id: childId },
      );
      if (cancelled) return;
      if (error) {
        setError(error.message);
        setData(null);
      } else {
        setData(data as unknown as ParentHubEngineSummary);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [childId]);

  return { data, loading, error };
}